import {
  Button,
  Grid,
  Modal,
  Popover,
  Radio,
  Result,
  Table,
  Tag,
  message,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Router from "../..";
import { useAppliedInfluencersByBrand } from "../../hooks/useCampaign";
import { useUserStore } from "../../../stores/userStore.js";
import { FiArrowLeft } from "react-icons/fi";
import PortOne from "@portone/browser-sdk/v2";

const STORE_ID = process.env.REACT_APP_PORT_ONE_STORE_ID;
const KRW_CHANNEL_KEY = process.env.REACT_APP_PORT_ONE_KRW_CHANNEL_KEY;
const USD_CHANNEL_KEY = process.env.REACT_APP_PORT_ONE_USD_CHANNEL_KEY;

const calculateSettlement = (payout) => {
  const settlementAmount = Number(payout);
  const incomeTax = Math.floor(settlementAmount * 0.03); // 소득세
  const taxAmount = incomeTax + Math.floor(incomeTax * 0.1); // 총 원청징수 (소득세 + 지방소득세)
  const netAmount = settlementAmount - taxAmount;

  return {
    settlementAmount, // 총 정산 금액
    taxAmount, // 총 세액
    netAmount, // 실지급 금액
  };
};

const formatCurrency = (value, currency = "KRW") => {
  const amount = Number(value) || 0;

  if (currency && currency !== "KRW") {
    return `${amount.toLocaleString()} ${currency}`;
  }

  return `${amount.toLocaleString()} 원`;
};

export const Payout = () => {
  const history = useHistory();
  const location = useLocation();

  const campaignId = new URLSearchParams(location.search).get("campaign_id");
  const [campaign] = useState(location.state?.campaign || null);
  const lockedMode =
    location.state?.settlementMode === "single" ||
    location.state?.settlementMode === "bulk"
      ? location.state.settlementMode
      : null;
  const selectedId = location.state?.selectedId;
  const [settlementMode, setSettlementMode] = useState(lockedMode ?? "single");
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);
  const [paymentPrepareLoading, setPaymentPrepareLoading] = useState(false);
  const { userInfo } = useUserStore();

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const { data } = useAppliedInfluencersByBrand({
    campaignId,
    status: "completed",
    enabled: !!campaignId,
  });
  const completedTargets = data?.result ?? []; // 정산이 가능한(completed) 전체 크리에이터

  const completedTargetIds = completedTargets.map((item) => item.id); // completed 상태인 크리에이터의 id
  const selectedTargets = completedTargets.filter((item) =>
    selectedTargetIds.includes(item.id)
  ); // 현재 선택된 크리에이터
  const effectiveTargets =
    settlementMode === "bulk" ? completedTargets : selectedTargets;

  const payoutPerTarget = Number(campaign?.payout) || 0;
  const totalPayoutAmount = payoutPerTarget * effectiveTargets.length;
  const campaignCurrency = campaign?.currency || "KRW";
  const companyBankName = userInfo?.type_info?.bank_name;

  const random = crypto.randomUUID().replace(/-/g, "").slice(0, 12);

  const handleBack = () => {
    history.goBack();
  };

  // 포트원 결제
  const portOnePayment = async () => {
    if (paymentPrepareLoading) return;

    setPaymentPrepareLoading(true);

    try {
      // 결제 준비
      const merchantUid = `mid_${Date.now()}_${random}`;
      const payerName = [
        userInfo?.user_info?.first_name,
        userInfo?.user_info?.last_name,
      ]
        .join(" ")
        .trim();
      const payerAddr = [
        userInfo?.user_info?.state_province,
        userInfo?.user_info?.city,
        userInfo?.user_info?.street_address,
        userInfo?.user_info?.address_line,
      ]
        .join(" ")
        .trim();
      const payerEmail = userInfo?.user_info?.email;

      if (!payerEmail) {
        message.error("결제 진행을 위해 구매자 이메일이 필요합니다.");
        return;
      }

      const { settlementAmount, taxAmount, netAmount } = calculateSettlement(
        campaign?.payout
      );

      const payload = {
        campaign_id: campaign.id,
        pg_name: campaignCurrency === "KRW" ? "kg_inicis" : "paypal",
        pay_method: "card",
        paid_amount: totalPayoutAmount,
        currency: campaignCurrency,
        payer_name: payerName,
        merchant_uid: merchantUid,
        payer_email: payerEmail,
        payer_tel: userInfo?.user_info?.phone,
        payer_addr: payerAddr || undefined,
        payer_postcode: userInfo?.user_info?.postal_code,
        settlement_items: effectiveTargets.map((item) => ({
          campaign_recruitment_id: item.id,
          payee_user_id: item.user_id,
          settlement_amount: settlementAmount,
          tax_type: "withholding",
          tax_amount: taxAmount,
          net_amount: netAmount,
          currency: campaign?.currency ?? "KRW",
        })),
      };

      // 서버에 결제 정보 저장
      const prepared = await Router.Api.createPayment(payload);
      if (!prepared) return;

      sessionStorage.setItem(
        `portone_payment:${merchantUid}`,
        JSON.stringify({
          id: prepared.id,
          amount: totalPayoutAmount,
          currency: campaignCurrency,
        })
      );

      // 포트원 결제창 호출
      await PortOne.requestPayment({
        storeId: STORE_ID,
        channelKey:
          campaignCurrency === "KRW" ? KRW_CHANNEL_KEY : USD_CHANNEL_KEY,
        paymentId: merchantUid,
        orderName: campaign.title,
        totalAmount: totalPayoutAmount,
        currency: campaignCurrency,
        payMethod: "CARD",
        customer: {
          fullName: payerName || undefined,
          email: payerEmail,
          phoneNumber: userInfo?.user_info?.phone,
        },
        redirectUrl: `${
          window.location.origin
        }/payments/return?campaign_id=${encodeURIComponent(campaign.id)}`,
        forceRedirect: true,
      });
    } catch (error) {
      message.error(error?.message || "결제창을 열지 못했습니다.");
    } finally {
      setPaymentPrepareLoading(false);
    }
  };

  // 임시 계좌이체
  const handlePayout = async () => {
    const payerName = userInfo?.type_info?.bank_holder_name;
    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: "입금자 정보 확인",
        width: 520,
        okText: "입금 계좌 보기",
        cancelText: "취소",
        content: (
          <div className="text-[14px] leading-6 text-[#4b5563]">
            <p>아래 정보는 서핑크리로 입금하실 때 확인용으로 사용됩니다.</p>
            <p className="mt-2">
              다음 단계에서 서핑크리 입금 계좌 정보를 안내해드립니다.
            </p>

            <div className="my-4 rounded-xl border border-[#ececec] bg-[#fafafa] p-4 text-[#111827] space-y-4">
              <div>
                <span className="font-semibold">입금자명</span>: {payerName}
              </div>
              <div>
                <span className="font-semibold">출금 은행명</span>:{" "}
                {companyBankName}
              </div>
            </div>

            <div className="space-y-2 text-[13px] text-[#4b5563]">
              <p>
                입금 후 자동 승인되지 않으며, 운영팀 확인 후 크리에이터 정산이
                진행됩니다.
              </p>
              <p>
                입금자 정보가 등록 내용과 다르면 입금 확인이 지연될 수 있습니다.
              </p>
            </div>
          </div>
        ),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;

    const paymentConfirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: "계좌이체",
        okText: "입금 완료",
        cancelText: "취소",
        width: 520,
        content: (
          <div className="space-y-4 pt-2 text-[14px] leading-6 text-[#4b5563]">
            <p>아래 계좌로 입금해 주세요.</p>

            <div className="rounded-2xl border border-[#ececec] bg-[#fafafa] p-5 text-[#111827]">
              <div className="space-y-2">
                <div>
                  <span className="font-semibold">회사명</span>: (주)금바다
                </div>
                <div>
                  <span className="font-semibold">은행명</span>: 국민은행
                  (Kookmin Bank)
                </div>
              </div>

              <div className="my-4 h-px bg-[#e5e7eb]" />

              <div className="space-y-4">
                {campaign.currency === "KRW" ? (
                  <div className="rounded-xl border border-[#ececec] bg-white px-4 py-3">
                    <p className="mb-1 text-[12px] font-semibold text-[#6b7280]">
                      국내 입금 계좌
                    </p>
                    <p className="text-[16px] font-bold tracking-[0.02em]">
                      818501-04-111681
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-[#ececec] bg-white px-4 py-3">
                    <p className="mb-1 text-[12px] font-semibold text-[#6b7280]">
                      해외 입금 계좌
                    </p>
                    <p className="text-[16px] font-bold tracking-[0.02em]">
                      929068-11-018517
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 text-[13px] text-[#6b7280]">
              <p className="text-red-500">
                실제 입금 후에만 '입금 완료' 버튼을 눌러 주세요.
              </p>
              <p>
                입금 후 자동 승인되지는 않으며, 운영팀이 확인한 뒤 정산이
                반영됩니다.
              </p>
              <p>문의: support@surfingcre.co.kr | 채널톡(Channel Talk)</p>
            </div>
          </div>
        ),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!paymentConfirmed) return;

    setPaymentPrepareLoading(true);
    try {
      const payerAddr = [
        userInfo?.user_info?.state_province,
        userInfo?.user_info?.city,
        userInfo?.user_info?.street_address,
        userInfo?.user_info?.address_line,
      ]
        .join(" ")
        .trim();

      const { settlementAmount, taxAmount, netAmount } = calculateSettlement(
        campaign?.payout
      );

      const merchantUid = `mid_${Date.now()}_${random}`;

      const payload = {
        campaign_id: campaign.id,
        bank_name: companyBankName,
        paid_amount: totalPayoutAmount,
        currency: campaignCurrency,
        payer_name: payerName || undefined,
        payer_email: userInfo?.user_info?.email,
        payer_tel: userInfo?.user_info?.phone,
        payer_addr: payerAddr || undefined,
        merchant_uid: merchantUid,
        payer_postcode: userInfo?.user_info?.postal_code,
        settlement_items: effectiveTargets.map((item) => ({
          campaign_recruitment_id: item.id,
          payee_user_id: item.user_id,
          settlement_amount: settlementAmount,
          tax_type: "withholding",
          tax_amount: taxAmount,
          net_amount: netAmount,
          currency: campaign?.currency ?? "KRW",
        })),
      };

      const res = await Router.Api.requestBankTransferPayment(payload);
      if (!res) return;

      message.success("결제 요청이 접수되었습니다.");
      history.replace("/payments/brand-payments-history");
    } finally {
      setPaymentPrepareLoading(false);
    }
  };

  useEffect(() => {
    if (settlementMode === "bulk") {
      setSelectedTargetIds(completedTargets.map((item) => item.id));
      return;
    }

    if (selectedId != null) {
      setSelectedTargetIds([selectedId]);
      return;
    }

    setSelectedTargetIds([]);
  }, [completedTargets, selectedId, settlementMode]);

  if (!campaign) {
    return (
      <div className="max-w-[900px] mx-auto">
        <Result
          status="warning"
          title="캠페인 정보를 찾을 수 없습니다."
          subTitle="캠페인 관리 화면에서 다시 진입해 주세요."
          extra={
            <Button
              type="primary"
              onClick={() => history.replace("/campaign/manage")}
            >
              캠페인 관리로 이동
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <div className="md:text-4xl text-3xl font-bold mb-10">캠페인 정산</div>
      <Button
        type="button"
        onClick={handleBack}
        size={screens.md ? "middle" : "small"}
        className="text-lg md:text-xl mb-5 inline-flex items-center gap-2 border border-[#e1e6ec] bg-white px-4 py-2 font-semibold text-[#343a40]"
      >
        <FiArrowLeft className="text-lg md:text-xl" />
        목록으로 돌아가기
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        <div className="space-y-6">
          <section className="bg-white rounded-2xl p-6 shadow-sm border border-[#ececec]">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between overflow-hidden">
              <div>
                <div className="text-[13px] text-[#666] mb-2">
                  정산 대상 캠페인
                </div>
                <div className="text-[16px] md:text-[20px] font-bold mb-2">
                  {campaign.title}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Tag color="blue">유가 캠페인</Tag>
                  <Tag color="green">
                    리워드 {formatCurrency(payoutPerTarget, campaignCurrency)}
                  </Tag>
                </div>
              </div>

              <div className="bg-[#f7f7f7] rounded-xl px-4 py-3 min-w-[220px]">
                <div className="text-[12px] text-[#666] mb-1">진행 기간</div>
                <div className="font-semibold">
                  {campaign.start_at
                    ? dayjs(campaign.start_at).format("YYYY-MM-DD")
                    : "-"}
                  {" ~ "}
                  {campaign.end_at
                    ? dayjs(campaign.end_at).format("YYYY-MM-DD")
                    : "-"}
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-6 shadow-sm border border-[#ececec]">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
              <div className="text-[16px] md:text-[20px] font-bold mb-1">
                {lockedMode === "single" ? "개별 정산" : "일괄 정산"}
              </div>
              {!lockedMode && (
                <Radio.Group
                  size="small"
                  value={settlementMode}
                  onChange={(e) => {
                    setSettlementMode(e.target.value);
                  }}
                >
                  <Radio.Button value="single">개별 정산</Radio.Button>
                  <Radio.Button value="bulk">일괄 정산</Radio.Button>
                </Radio.Group>
              )}
            </div>

            <Table
              rowKey="id"
              dataSource={completedTargets}
              pagination={false}
              className="overflow-x-auto"
              rowSelection={
                settlementMode === "single"
                  ? {
                      type: "checkbox",
                      selectedRowKeys: selectedTargetIds,
                      onChange: (keys) => setSelectedTargetIds(keys),
                      preserveSelectedRowKeys: true,
                    }
                  : {
                      type: "checkbox",
                      selectedRowKeys: completedTargetIds,
                      preserveSelectedRowKeys: true,
                      getCheckboxProps: () => ({ disabled: true }),
                      getTitleCheckboxProps: () => ({ disabled: true }),
                    }
              }
              columns={[
                {
                  title: <span className="whitespace-nowrap">닉네임</span>,
                  dataIndex: "nickname",
                  key: "nickname",
                  align: "center",
                  width: 120,
                  render: (value) => value || "닉네임 없음",
                },
                {
                  title: <span className="whitespace-nowrap">미션</span>,
                  dataIndex: "mission_url",
                  key: "mission_url",
                  align: "center",
                  width: 150,
                  render: (value) => {
                    const urls = value
                      .split(",")
                      .map((url) => url.trim())
                      .filter(Boolean);

                    return (
                      <Popover
                        title="미션 URL"
                        trigger="click"
                        content={
                          <div className="max-w-[400px]">
                            {urls.map((url, index) => (
                              <a
                                key={`${url}-${index}`}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block break-all text-[12px] text-[#1890ff] no-underline mb-2 last:mb-0"
                              >
                                {url}
                              </a>
                            ))}
                          </div>
                        }
                      >
                        <Button size="small" type="link">
                          미션 확인하기
                        </Button>
                      </Popover>
                    );
                  },
                },
                {
                  title: "완료일",
                  dataIndex: "updated_at",
                  key: "updated_at",
                  align: "center",
                  width: 140,
                  render: (value) =>
                    value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-",
                },
                {
                  title: "상태",
                  dataIndex: "recruitment_status",
                  key: "recruitment_status",
                  align: "center",
                  width: 110,
                  render: () => (
                    <Tag color="green" style={{ marginInlineEnd: 0 }}>
                      완료
                    </Tag>
                  ),
                },
              ]}
            />
          </section>
        </div>

        <div className="space-y-6">
          <section className="bg-[#171717] text-white rounded-2xl p-6 shadow-sm">
            <div className="text-[14px] text-white/70 mb-2">정산 요약</div>
            <div className="text-[22px] md:text-[26px] font-bold mb-6">
              {settlementMode === "single" ? "개별 정산" : "일괄 정산"}
            </div>

            <div className="space-y-4 text-[15px]">
              <div className="flex items-center justify-between">
                <span className="text-white/70">선택 대상</span>
                <span className="font-semibold">
                  {effectiveTargets.length.toLocaleString()}명
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/70">인당 정산금</span>
                <span className="font-semibold">
                  {formatCurrency(payoutPerTarget, campaignCurrency)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/70">총 정산 예정 금액</span>
                <span className="font-semibold">
                  {formatCurrency(totalPayoutAmount, campaignCurrency)}
                </span>
              </div>
            </div>

            <div className="h-px bg-white/15 my-6" />

            <div className="flex items-center justify-between text-[18px] font-bold">
              <span>결제 예정 금액</span>
              <span>{formatCurrency(totalPayoutAmount, campaignCurrency)}</span>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-6 shadow-sm border border-[#ececec]">
            <div className="text-[18px] font-bold mb-3">안내</div>
            <ul className="text-[14px] text-[#666] leading-7 list-disc pl-5">
              <li>정산 대상은 완료 처리된 유가 캠페인 건만 포함됩니다.</li>
              {lockedMode === "single" && (
                <li>정산할 인플루언서를 표에서 선택해 주세요.</li>
              )}
              {lockedMode === "bulk" && (
                <li>완료된 모든 대상이 자동으로 포함됩니다.</li>
              )}
            </ul>
            {!(campaignId === "827f40e4-0621-4bc3-b723-b085e9b715d1") && (
              <>
                <p className="mt-2 font-semibold text-[#444]">
                  현재 결제 시스템 전환 작업 중으로, 정산은 임시로 계좌이체
                  방식으로 진행되고 있습니다.
                </p>
                <p className="font-semibold text-red-500">
                  계좌이체 후 자동 승인되지 않으며, 운영팀이 입금 내역을 확인한
                  뒤 크리에이터 정산이 진행됩니다.
                </p>
              </>
            )}

            <div className="flex gap-3 mt-6">
              {!(campaignId === "827f40e4-0621-4bc3-b723-b085e9b715d1") ? (
                <Button
                  block
                  type="primary"
                  onClick={handlePayout}
                  // onClick={portOnePayment}
                  disabled={effectiveTargets.length === 0}
                  loading={paymentPrepareLoading}
                >
                  {/* 결제 자동화 도입 준비 중입니다. */}
                  계좌이체 결제하기
                </Button>
              ) : (
                <Button
                  block
                  type="primary"
                  onClick={portOnePayment}
                  disabled={effectiveTargets.length === 0}
                  loading={paymentPrepareLoading}
                >
                  결제하기
                </Button>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
